/**
 * 解决方案分类展示样式配置（纯展示层配置，非业务数据）。
 *
 * 当接口未返回某分类的自定义展示配置，或该分类是未来新增但尚未同步到本文件时，
 * 所有查找访问均应通过下方导出的回退函数/常量取值，避免遗漏同步导致渲染异常。
 */

/** 四个分类的主题色（Active_Category_Set） */
export const CATEGORY_ACCENT_COLOR: Record<string, string> = {
  "info-security": "#1a56db",
  "it-infrastructure": "#0891b2",
  "cloud-xinda": "#7c3aed",
  "ai-business": "#ea580c",
};

/** 分类主题色缺省回退值（未配置分类统一使用同一固定色） */
export const DEFAULT_ACCENT_COLOR = "#6b7280";

/**
 * 获取分类主题色，未配置时返回唯一固定的 {@link DEFAULT_ACCENT_COLOR}。
 * Validates: Requirements 9.2, 9.3
 */
export function getAccentColor(categoryId: string): string {
  return CATEGORY_ACCENT_COLOR[categoryId] ?? DEFAULT_ACCENT_COLOR;
}

/**
 * 关联案例分类映射：解决方案分类标识 → 案例分类标识集合。
 * 案例分类与方案分类对齐为四类：`cloud-xinda`/`ai-business` 的案例分类标识与方案分类同名；
 * IT基础架构对应案例分类 `infrastructure`。`smart-integration` 是已并入基础架构的旧案例分类，
 * 这里仍保留映射，保证数据库迁移脚本尚未执行的环境里旧案例不会从关联案例中消失。
 * Validates: Requirements 11.4, 11.5
 */
export const CASE_SOLUTION_MAP: Record<string, string[]> = {
  "info-security": ["info-security"],
  "it-infrastructure": ["infrastructure", "smart-integration"],
  "cloud-xinda": ["cloud-xinda"],
  "ai-business": ["ai-business"],
};

// ─── 导航（产品 / 案例下拉）与案例页共用的分类展示配置 ─────────────────────────────

/** 方案分类在导航下拉中的展示信息（名称、图标、简介、浅色底） */
export interface CategoryNavDisplay {
  name: string;
  /** 筛选标签等空间紧凑处使用的简称 */
  shortName: string;
  icon: string;
  desc: string;
  light: string;
}

export const CATEGORY_NAV_DISPLAY: Record<string, CategoryNavDisplay> = {
  "info-security": { name: "信息安全综合防护", shortName: "信息安全", icon: "ri-shield-keyhole-line", desc: "防火墙、零信任、SOC等安全防护方案", light: "#eff6ff" },
  "it-infrastructure": { name: "IT基础架构", shortName: "IT基础架构", icon: "ri-server-line", desc: "数据中心、云计算、超融合等基础架构方案", light: "#ecfeff" },
  "cloud-xinda": { name: "云信达", shortName: "云信达", icon: "ri-cloud-line", desc: "云主机、云存储、容灾备份等云服务方案", light: "#f5f3ff" },
  "ai-business": { name: "AI业务", shortName: "AI业务", icon: "ri-robot-line", desc: "智能分析、机器学习平台等AI业务方案", light: "#fff7ed" },
};

/** 未配置分类的导航展示回退值 */
export const DEFAULT_CATEGORY_NAV_DISPLAY: CategoryNavDisplay = {
  name: "",
  shortName: "",
  icon: "ri-settings-line",
  desc: "",
  light: "#f3f4f6",
};

/**
 * 查导航展示配置；分类不在配置中时用传入的回退名称/简介（通常取接口返回的 title/subtitle），
 * 保证后续新增分类无需同步本文件也能正常渲染。
 */
export function getCategoryNavDisplay(
  categoryId: string,
  fallback?: { name?: string; desc?: string; icon?: string },
): CategoryNavDisplay {
  const known = CATEGORY_NAV_DISPLAY[categoryId];
  if (known) return known;
  return {
    ...DEFAULT_CATEGORY_NAV_DISPLAY,
    name: fallback?.name ?? categoryId,
    shortName: fallback?.name ?? categoryId,
    desc: fallback?.desc ?? "",
    icon: fallback?.icon || DEFAULT_CATEGORY_NAV_DISPLAY.icon,
  };
}

/** 案例分类标识 → 对应的方案分类标识（案例的“基础架构”对应方案的 it-infrastructure，其余同名） */
export const CASE_TO_SOLUTION_CATEGORY: Record<string, string> = {
  "info-security": "info-security",
  "infrastructure": "it-infrastructure",
  "cloud-xinda": "cloud-xinda",
  "ai-business": "ai-business",
};

/**
 * 案例分类的展示样式。Tailwind 只会生成源码中以完整字面量出现的类名，
 * 因此这里把 bg/text/border 类名完整写出，不用字符串拼接。
 */
export interface CaseCategoryStyle {
  bg: string;
  text: string;
  border: string;
  hex: string;
}

export const CASE_CATEGORY_STYLE: Record<string, CaseCategoryStyle> = {
  "info-security": { bg: "bg-[#1a56db]/8", text: "text-[#1a56db]", border: "border-[#1a56db]/20", hex: "#1a56db" },
  "infrastructure": { bg: "bg-[#0891b2]/8", text: "text-[#0891b2]", border: "border-[#0891b2]/20", hex: "#0891b2" },
  "cloud-xinda": { bg: "bg-[#7c3aed]/8", text: "text-[#7c3aed]", border: "border-[#7c3aed]/20", hex: "#7c3aed" },
  "ai-business": { bg: "bg-[#ea580c]/8", text: "text-[#ea580c]", border: "border-[#ea580c]/20", hex: "#ea580c" },
};

/** 案例分类样式回退值（未配置分类，含已并入基础架构的旧分类 smart-integration） */
export const DEFAULT_CASE_CATEGORY_STYLE: CaseCategoryStyle = {
  bg: "bg-gray-50",
  text: "text-gray-600",
  border: "border-gray-100",
  hex: "#6b7280",
};

export function getCaseCategoryStyle(caseCategoryId: string): CaseCategoryStyle {
  return CASE_CATEGORY_STYLE[caseCategoryId] ?? DEFAULT_CASE_CATEGORY_STYLE;
}

/**
 * 未知分类（不在展示配置中）的默认图标标识，供导航下拉与首页区块在
 * 分类展示配置缺失时回退使用。
 */
export const DEFAULT_CATEGORY_ICON = "ri-settings-line";

// ─── AI业务 统一入口 ─────────────────────────────────────────────────────────────
// 本文件保持无 import，供 Navbar、页面分流、SEO 等多处引用而不产生循环依赖。

/** AI业务分类标识（解决方案 / 产品 / 案例三处菜单判定值一致） */
export const AI_BUSINESS_CATEGORY_ID = "ai-business";

/** AI业务首页地址：Navbar 三个下拉菜单的「AI业务」分类标题统一进入此处 */
export const AI_OVERVIEW_URL = "/solutions?category=ai-business";

/**
 * 分类标题入口地址：仅 `ai-business` 统一到 {@link AI_OVERVIEW_URL}，
 * 其余分类保持调用方传入的原有地址。
 * Validates: Requirements 1.1, 1.2, 1.3, 1.7
 */
export function getCategoryEntryHref(catId: string, fallbackHref: string): string {
  return catId === AI_BUSINESS_CATEGORY_ID ? AI_OVERVIEW_URL : fallbackHref;
}
